import {
  AfterViewInit,
  ChangeDetectorRef,
  ComponentRef,
  Directive,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Renderer2,
  Self,
  SimpleChanges,
  ViewContainerRef,
} from '@angular/core';
import { NgControl } from '@angular/forms';

import { Subject, fromEvent } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { ClearButtonComponent, HintComponent, LabelComponent, MatchTypeComponent, ValidationComponent } from '@core/components/controls';
import { MatchTypes } from '@core/interfaces';

@Directive({
  selector: 'input[zTextInput]',
})
export class TextInputDirective implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  destroy$ = new Subject<boolean>();

  @Input() id: string;

  @Input() styleClass?: any;
  @Input() uppercase: boolean = false;
  @Input() numeric: boolean = false;
  @Input() currency: boolean = false;
  @Input() decimal: boolean = false;
  @Input() allowedCharacters: string[] = [];

  @Input() label: string;
  @Input() hint: string;

  @Input() displayValidationErrors: boolean = true;

  @Input() clearButton: boolean = false;
  @Input() matchTypes: boolean = false;
  @Input() defaultMatchType: MatchTypes = null;
  @Input() matchTypeDataType: 'string' | 'number' | 'date';

  @Input() alignRight: boolean = false;

  @Output() clear = new EventEmitter<void>();
  @Output() matchTypeChange = new EventEmitter<string>();

  @HostBinding('class') class = `z-text-input p-inputtext p-component p-element`;

  @HostBinding('class.uppercase') get isUppercase() {
    return this.uppercase;
  }

  @HostBinding('class.has-clear-button') get hasClearbutton() {
    return !!this.clearButton;
  }

  @HostBinding('class.show-clear-button') get hasValue() {
    return !!this.ngControl.control.value;
  }

  @HostBinding('class.with-label') get withLabel() {
    return !!this.label;
  }

  @HostBinding('class.align-right') get alignRightClass() {
    return this.alignRight;
  }

  @HostBinding('id') elementId: string;

  wrapperRef: HTMLDivElement;
  labelComponentRef: ComponentRef<LabelComponent>;
  clearButtonComponentRef: ComponentRef<ClearButtonComponent>;
  hintComponentRef: ComponentRef<HintComponent>;
  matchTypeComponentRef: ComponentRef<MatchTypeComponent>;
  validationComponentRef: ComponentRef<ValidationComponent>;

  parent: HTMLElement;

  constructor(
    private renderer: Renderer2,
    private elementRef: ElementRef,
    private changeDetectorRef: ChangeDetectorRef,
    private viewContainerRef: ViewContainerRef,
    @Optional() @Self() public ngControl: NgControl
  ) {}

  ngOnInit() {
    if (this.id) {
      this.elementId = this.id;
    } else {
      this.elementId = `z-text-input-${this.ngControl.name}`;
    }

    if (this.currency || this.decimal) {
      this.numeric = true;

      if (this.allowedCharacters.length === 0) {
        this.allowedCharacters = ['.', '-'];
      }
    }

    this.ngControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.changeDetectorRef.markForCheck();
      this.changeDetectorRef.detectChanges();
    });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.label && !changes.label.firstChange) {
      this.label = changes.label.currentValue;

      if (!this.labelComponentRef) {
        this.prependLabel();
      } else {
        this.labelComponentRef.instance.text = this.label;
        this.labelComponentRef.instance.invalid = !this.ngControl.pristine && this.ngControl.invalid;
        this.labelComponentRef.instance.update();
      }
    }

    if (changes.hint && !changes.hint.firstChange) {
      this.hint = changes.hint.currentValue;

      if (!this.hintComponentRef) {
        this.appendHint();
      } else {
        this.hintComponentRef.instance.text = this.hint;
        this.hintComponentRef.instance.invalid = !this.ngControl.pristine && this.ngControl.invalid;
        this.hintComponentRef.instance.update();
      }
    }
  }

  ngAfterViewInit() {
    this.parent = this.elementRef.nativeElement.parentNode;

    setTimeout(() => {
      this.wrapInput();
      this.prependLabel();
      this.appendClearButton();
      this.appendHint();
      this.appendMatchTypes();
      this.appendValidation();
    }, 0);

    // force input to be uppercase
    if (this.uppercase) {
      fromEvent(this.elementRef.nativeElement, 'keyup')
        .pipe(
          filter(() => !!this.ngControl.control.value),
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          if (this.ngControl?.control?.value && typeof this.ngControl?.control?.value === 'string') {
            this.ngControl.control.setValue(this.ngControl?.control?.value?.toUpperCase());
          }
        });
    }

    // if field is numeric, apply validations when inputting / pasting / dropping data
    if (this.numeric) {
      fromEvent(this.elementRef.nativeElement, 'keydown')
        .pipe(
          filter((event: KeyboardEvent) => !this.isSpecialKey(event)),
          filter((event: KeyboardEvent) => !this.isNumericOrAllowed(event)),
          takeUntil(this.destroy$)
        )
        .subscribe((event: KeyboardEvent) => {
          event.preventDefault();
        });

      fromEvent(this.elementRef.nativeElement, 'paste')
        .pipe(takeUntil(this.destroy$))
        .subscribe((event: ClipboardEvent) => {
          event.preventDefault();
          this.removeInvalidClipboardCharacters(event);
        });

      fromEvent(this.elementRef.nativeElement, 'drop')
        .pipe(takeUntil(this.destroy$))
        .subscribe((event: DragEvent) => {
          event.preventDefault();
          this.removeInvalidDragCharacters(event);
        });
    }
  }

  wrapInput() {
    this.wrapperRef = this.renderer.createElement('div');
    this.renderer.addClass(this.wrapperRef, 'z-text-input-wrapper');
    this.renderer.insertBefore(this.parent, this.wrapperRef, this.elementRef.nativeElement);
    this.renderer.appendChild(this.wrapperRef, this.elementRef.nativeElement);
  }

  prependLabel() {
    // prepend label if enabled
    if (this.label) {
      this.labelComponentRef = this.viewContainerRef.createComponent<LabelComponent>(LabelComponent);
      this.labelComponentRef.instance.id = this.elementId;
      this.labelComponentRef.instance.text = this.label;

      this.renderer.insertBefore(this.parent, this.labelComponentRef.location.nativeElement, this.wrapperRef);

      this.labelComponentRef.instance.update();
    }
  }

  appendClearButton() {
    // append clear button if enabled
    if (this.clearButton) {
      this.renderer.addClass(this.parent, 'relative');

      this.clearButtonComponentRef = this.viewContainerRef.createComponent<ClearButtonComponent>(ClearButtonComponent);
      this.clearButtonComponentRef.instance.click.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.clear.emit();
        this.ngControl.control.setValue('');
        this.ngControl.control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
        this.changeDetectorRef.markForCheck();
      });

      this.renderer.appendChild(this.wrapperRef, this.clearButtonComponentRef.location.nativeElement);
    }
  }

  appendHint() {
    // append hint if enabled
    if (this.hint) {
      this.hintComponentRef = this.viewContainerRef.createComponent<HintComponent>(HintComponent);
      this.hintComponentRef.instance.text = this.hint;
    }
  }

  appendValidation() {
    // append validation if enabled
    if (this.displayValidationErrors) {
      this.validationComponentRef = this.viewContainerRef.createComponent<ValidationComponent>(ValidationComponent);

      this.validationComponentRef.instance.control = this.ngControl.control;
    }
  }

  appendMatchTypes() {
    // append match types dropdown if enabled
    if (this.matchTypes) {
      this.renderer.addClass(this.parent, 'relative');

      this.matchTypeComponentRef = this.viewContainerRef.createComponent<MatchTypeComponent>(MatchTypeComponent);

      // if the field is numeric and there are no allowed characters defined, or the allowed characters
      // list only contains a decimal or dash then set the matchtype component to number mode
      if (!this.matchTypeDataType) {
        if (this.numeric && (this.allowedCharacters.length === 0 || /^[\d|\.|-]*$/g.test(this.allowedCharacters.join('')))) {
          this.matchTypeComponentRef.instance.type = 'number';
        }
      } else {
        this.matchTypeComponentRef.instance.type = this.matchTypeDataType;
      }

      if (!this.label) {
        this.matchTypeComponentRef.instance.controlWithoutLabel = true;
      }

      if (this.defaultMatchType) {
        this.matchTypeComponentRef.instance.defaultMatchType = this.defaultMatchType;
      }

      this.matchTypeComponentRef.instance.change.pipe(takeUntil(this.destroy$)).subscribe((matchType) => {
        this.matchTypeChange.emit(matchType);
      });
    }
  }

  removeInvalidClipboardCharacters(event: ClipboardEvent) {
    const validCharacters: string[] = ['\\d'].concat(this.allowedCharacters);
    const re = new RegExp(`[^${validCharacters.join('|')}]`, 'gi');

    const data = event.clipboardData.getData('text/plain').replace(re, '');
    document.execCommand('insertText', false, data);
  }

  removeInvalidDragCharacters(event: DragEvent) {
    const validCharacters: string[] = ['\\d'].concat(this.allowedCharacters);
    const re = new RegExp(`[^${validCharacters.join('|')}]`, 'gi');

    const data = event.dataTransfer.getData('text').replace(re, '');
    this.elementRef.nativeElement.focus();

    document.execCommand('insertText', false, data);
  }

  isNumericOrAllowed(event: KeyboardEvent) {
    const validCharacters: string[] = ['\\d'].concat(this.allowedCharacters);
    const re = new RegExp(`[${validCharacters.join('|')}]`, 'gi');

    return re.test(event.key);
  }

  isSpecialKey(event: KeyboardEvent) {
    const navigationKeys = [
      'ArrowLeft',
      'ArrowRight',
      'Backspace',
      'Clear',
      'Copy',
      'Delete',
      'End',
      'Enter',
      'Escape',
      'Home',
      'Paste',
      'Tab',
    ];

    if (
      navigationKeys.includes(event.key) || // allow navigation keys: backspace, delete, arrows, etc
      (event.key === 'a' && (event.metaKey || event.ctrlKey)) || // allow: ctrl+a / cmd+a
      (event.key === 'c' && (event.metaKey || event.ctrlKey)) || // allow: ctrl+c / cmd+c
      (event.key === 'v' && (event.metaKey || event.ctrlKey)) || // allow: ctrl+v / cmd+v
      (event.key === 'x' && (event.metaKey || event.ctrlKey)) // allow: ctrl+x / cmd+x
    ) {
      return true;
    }

    return false;
  }
}
